import { createSelector } from '@reduxjs/toolkit';
import { createPatch } from 'rfc6902';
import { getPropertyByPath } from '../composer/utils/objects';
import type { EditorRootState } from './types';
import isWebOnlyApp from '../composer/utils/isWebOnlyApp';

export const selectIsProjectEditable = (state: EditorRootState) => state.editor.editable;
export const selectProjectId = (state: EditorRootState) => state.editor.projectId;
export const selectProjectType = (state: EditorRootState) => state.editor.projectType;
export const selectProjectVersion = (state: EditorRootState) => state.editor.version;
export const selectEditorIsSaving = (state: EditorRootState) => state.editor.savingVersion != null;
export const selectEditorHasConflicts = (state: EditorRootState) => state.editor.conflictVersion != null;
export const selectCurrentDescriptor = <Descriptor>(state: EditorRootState<Descriptor>) =>
  state.editor.currentDescriptor;
export const selectRemoteDescriptor = <Descriptor>(state: EditorRootState<Descriptor>) => state.editor.remoteDescriptor;
export const selectUndoStackSize = (state: EditorRootState) => state.editor.undoStack.length;
export const selectRedoStackSize = (state: EditorRootState) => state.editor.redoStack.length;
export const selectUndoNavigationPath = (state: EditorRootState) => state.editor.undoStack.at(-1)?.navigationPath;
export const selectRedoNavigationPath = (state: EditorRootState) => state.editor.redoStack.at(-1)?.navigationPath;
export const selectEditorFailedToInitialize = (state: EditorRootState) => state.editor.failedToInitialize;
export const selectInitialMigrationDiffPatch = (state: EditorRootState) => state.editor.initialMigrationDiffPatch;

export const selectCurrentDescriptorDiffPatch = createSelector(
  selectRemoteDescriptor,
  selectCurrentDescriptor,
  (remote, current) => {
    if (!remote || !current) {
      return null;
    }
    return createPatch(remote, current);
  },
);

export const selectCurrentDescriptorReverseDiffPatch = createSelector(
  selectRemoteDescriptor,
  selectCurrentDescriptor,
  (remote, current) => {
    if (!remote || !current) {
      return null;
    }
    return createPatch(current, remote);
  },
);

export const selectEditorHasUnsavedChanges = (state: EditorRootState) => {
  const patch = selectCurrentDescriptorDiffPatch(state);
  const initialMigrationDiffPatch = selectInitialMigrationDiffPatch(state);
  return patch != null && patch.length > (initialMigrationDiffPatch?.length || 0);
};

/**
 * Creates a selector (function) that returns the value at the given property
 * path in the current descriptor state.
 * @param path deep property path in the descriptor structure
 * @returns value at the given path in the current descriptor structure
 */
export function createDescriptorPathSelector<Value = unknown>(path: (string | number)[]) {
  return function selectCurrentDescriptorProperty(state: EditorRootState) {
    return getPropertyByPath(selectCurrentDescriptor(state), path) as Value;
  };
}

export const selectIsWebOnlyApp = (state: EditorRootState) =>
  state.editor.projectType ? isWebOnlyApp(state.editor.projectType) : null;

/**
 * Selects the mobile service configuration from editor state
 */
export const selectMobileServiceConfig = (state: EditorRootState) => state.editor.mobileServiceConfig;
